/** @format */

import { Col, Row } from 'antd';
import parser from 'html-react-parser';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { BsCheck } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Apis from '../../../../../api';
import AccountCreated from '../../../../../assets/images/accountCreate.svg';
import useIsTeamMember from '../../../../../hooks/useIsTeamMember';
import { HEADERMENU_PATH } from '../../../../../routes/RoutesURL';
import { RootState } from '../../../../../store/rootReducer';
import HSButton from '../../../../common/Button';
import LoginActions from '../../../../login/redux/actions';
import MessageActions from '../../../../message/redux/actions';
import { ONBOARDING_PATH } from '../../Onbording';
import OnboardingCard from '../common/Card';
import OnboardingContainer from '../common/Container';
import { PreferencesKeys, investmentPreferencesList } from './Constant';

interface selectedPreferences {
    key: PreferencesKeys;
    data: string[];
}

type Props = {
    preferencesIndex?: number;
    setPreferencesIndex?: (f: number) => void;
    isSetting?: boolean;
    onClickCancel?: () => void;
};

const InvestmentPreferences: React.FC<Props> = ({
    preferencesIndex,
    setPreferencesIndex,
    isSetting,
    onClickCancel,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isTeamMember: boolean = useIsTeamMember();

    const uuid = useSelector((state: RootState) => state.user.uuid);
    const investorType = useSelector((state: RootState) => state.user.investor?.investorType);
    const preferences = useSelector((state: RootState) => state.user.investor?.preferences);
    //const investorType = useSelector((state: RootState) => state.user.investor?.investorType);
    //const kyc = useSelector((state: RootState) => state.user.investor?.kyc);

    const [selectedTypes, setTypes] = useState<selectedPreferences[] | undefined>(undefined);
    const [dontKnow, setDontKnow] = useState(false);
    const [activeStep, setActiveStep] = useState<number>(1);

    const [loader, setLoader] = useState(false);

    useLayoutEffect(() => {
        if (preferencesIndex) {
            setActiveStep(preferencesIndex);
        }
        return () => {
            setActiveStep(1);
        };
    }, [preferencesIndex]);

    useLayoutEffect(() => {
        if (preferences) {
            const index = investmentPreferencesList.findIndex((i) => i.step === activeStep);
            if (index !== -1) {
                const s: selectedPreferences[] = [];
                const data = investmentPreferencesList[index].data;
                data.forEach((i) => {
                    const list = preferences[i.type] || [];
                    if (list.indexOf(`I Don't Know`) !== -1) {
                        setDontKnow(true);
                        s.push({
                            key: i.type,
                            data: [],
                        });
                    } else {
                        s.push({
                            key: i.type,
                            data: list,
                        });
                    }
                });
                setTypes(s);
            }
        }
        return () => {
            setTypes(undefined);
        };
    }, [preferences, activeStep]);

    const handleChecked = (name: `I Don't Know` | 'selectAll', list: string[], key: PreferencesKeys) => {
        if (isTeamMember) {
            return;
        }
        if (name === `I Don't Know`) {
            setDontKnow(!dontKnow);
            let index = selectedTypes?.findIndex((d) => d.key === key);
            if (index === undefined) {
                index = -1;
            }
            let data: selectedPreferences[] = [...(selectedTypes || [])];
            if (index === -1) {
                data.push({ key: key, data: [] });
                index = data.length - 1;
            } else if (list.length !== data[index].data.length) {
                data[index].data = [];
                setTypes(data);
            } else {
                setTypes(undefined);
            }
        } else if (name === 'selectAll') {
            setDontKnow(false);
            let index = selectedTypes?.findIndex((d) => d.key === key);
            if (index === undefined) {
                index = -1;
            }
            let data: selectedPreferences[] = [...(selectedTypes || [])];
            if (index === -1) {
                data.push({ key: key, data: [] });
                index = data.length - 1;
            }
            if (list.length !== data[index].data.length) {
                data[index].data = list;
                setTypes(data);
            } else {
                data[index].data = [];
                setTypes(data);
            }
        }
    };

    const singleSelected = (key: PreferencesKeys, value: string, isMultipleSelect: boolean) => {
        if (isTeamMember) {
            return;
        }
        let index = selectedTypes?.findIndex((d) => d.key === key);
        setDontKnow(false);
        if (index === undefined) {
            index = -1;
        }
        let data: selectedPreferences[] = [...(selectedTypes || [])];
        if (index === -1) {
            data.push({ key: key, data: [] });
            index = data.length - 1;
        }
        const valueIndex = data[index].data.indexOf(value);
        let l = [...data[index].data];
        if (valueIndex === -1) {
            if (!isMultipleSelect) {
                l = [];
            }
            l.push(value);
        } else {
            l.splice(valueIndex, 1);
        }
        data[index].data = l;
        setTypes(data);
    };

    const getSelectedCheckboxStyle = () => {
        return { backgroundColor: '#27AE60', borderColor: '#27AE60' };
    };
    const navigateToOnboarding = () => {
        if (investorType !== 'Institutional') {
            navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.ACCREDITED_INVESTOR}`);
        } else {
            navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.KYC}`);
        }
    };

    const onClickNext = () => {
        if (isTeamMember) {
            if (activeStep === 5) {
                navigateToOnboarding();
                return;
            } else {
                setActiveStep(activeStep + 1);

                if (setPreferencesIndex) {
                    setPreferencesIndex(activeStep + 1);
                }
                return;
            }
        }

        let apiData: selectedPreferences[] = [];
        if (!selectedTypes) {
            const investmentData = investmentPreferencesList.find((d) => d.step === activeStep);
            if (investmentData) {
                investmentData.data.forEach((i) => {
                    apiData.push({ key: i.type, data: [] });
                });
            }
        } else {
            apiData = [...selectedTypes];
        }
        if (uuid && apiData) {
            setLoader(true);
            let obj: any = {};
            apiData.forEach((s) => (obj[s.key] = dontKnow ? [`I Don't Know`] : s.data || []));
            Apis.updateInvestorPreferences(uuid, { preferences: obj })
                .then(({ data }) => {
                    dispatch(LoginActions.updateInvestorInfo(data));

                    if (activeStep === 5) {
                        if (!isSetting) {
                            navigateToOnboarding();
                            setActiveStep(1);

                            if (setPreferencesIndex) {
                                setPreferencesIndex(1);
                            }
                        } else {
                            if (onClickCancel) {
                                onClickCancel();
                            }
                        }
                    } else {
                        setActiveStep(activeStep + 1);

                        if (setPreferencesIndex) {
                            setPreferencesIndex(activeStep + 1);
                        }
                    }
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setLoader(false);
                    setDontKnow(false);
                });
        } else {
            if (activeStep === 5) {
                setActiveStep(6);

                setDontKnow(false);
                if (setPreferencesIndex) {
                    setPreferencesIndex(6);
                }
            } else {
                setActiveStep(activeStep + 1);

                setDontKnow(false);
                if (setPreferencesIndex) {
                    setPreferencesIndex(activeStep + 1);
                }
            }
        }
    };

    const onClickskipKYC = () => {
        if (investorType !== 'Institutional') {
            navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.ACCREDITED_INVESTOR}`);
        } else {
            let selectedType = 'Manual';
            // if (investorType === 'Individual Accredited') {
            //     if (!kyc || Object.keys(kyc).length === 0) {
            //         selectedType = 'Option';
            //     }
            // }
            navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.KYC}`, {
                state: { selectedType },
            });
        }
    };
    const translatePercentageCalculate = useMemo(() => {
        return `translateX(${-investmentPreferencesList.findIndex((d) => d.step === activeStep) * 100}%)`;
    }, [activeStep]);

    return (
        <OnboardingContainer>
            <div className="overflow-x-hidden">
                <div
                    className="flex transition-transform ease-in-out duration-300"
                    style={{
                        transform: translatePercentageCalculate,
                    }}
                >
                    {investmentPreferencesList?.map((d, i) => (
                        <div key={i} className={`flex-shrink-0 w-full px-[10px]`}>
                            {d.step === activeStep ? (
                                <div id="investPreferences" key={`invest_preferences_step_${i + 1}`}>
                                    {d.data &&
                                        d.data.map((row, i) => (
                                            <div key={`row_${i}`}>
                                                <div className="md:flex md:justify-between">
                                                    <p className="page_title_onboarding pb-2 pt-6">{row.headText}</p>
                                                    <div className="flex WorkSans">
                                                        {row.iDontKnow && (
                                                            <p className="md:pt-6 pr-4 md:pr-1 flex items-center text-base md:text-xs text-color-404040 WorkSans">
                                                                <span
                                                                    onClick={() =>
                                                                        handleChecked(
                                                                            `I Don't Know`,
                                                                            row.list.map((l) => l.value),
                                                                            row.type,
                                                                        )
                                                                    }
                                                                    style={dontKnow ? getSelectedCheckboxStyle() : {}}
                                                                    className="h-5 w-5  border border-[#27AE60] mx-1 flex justify-center items-center cursor-pointer"
                                                                >
                                                                    {dontKnow && (
                                                                        <BsCheck size={'1.5rem'} color="#fff" />
                                                                    )}
                                                                </span>
                                                                I Don’t Know
                                                            </p>
                                                        )}
                                                        {row.selectAll && (
                                                            <p className="md:pt-6 flex items-center text-base md:text-xs text-color-404040 WorkSans">
                                                                <span
                                                                    onClick={() =>
                                                                        handleChecked(
                                                                            'selectAll',
                                                                            row.list.map((l) => l.value),
                                                                            row.type,
                                                                        )
                                                                    }
                                                                    style={
                                                                        (
                                                                            selectedTypes?.find(
                                                                                (s) => s.key === row.type,
                                                                            )?.data || []
                                                                        ).length === row.list.length
                                                                            ? getSelectedCheckboxStyle()
                                                                            : {}
                                                                    }
                                                                    className="h-5 w-5  border border-[#27AE60] mx-1 flex justify-center items-center cursor-pointer"
                                                                >
                                                                    {(
                                                                        selectedTypes?.find((s) => s.key === row.type)
                                                                            ?.data || []
                                                                    ).length === row.list.length && (
                                                                        <BsCheck size={'1.5rem'} color="#fff" />
                                                                    )}
                                                                </span>
                                                                Select All
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="py-4">
                                                    <Row gutter={[16, 16]}>
                                                        {row.list &&
                                                            row.list.map((s, index) => (
                                                                <Col
                                                                    xs={12}
                                                                    md={12}
                                                                    lg={8}
                                                                    xl={6}
                                                                    xxl={6}
                                                                    key={`${s}_${index}`}
                                                                >
                                                                    <OnboardingCard
                                                                        onClick={() =>
                                                                            singleSelected(
                                                                                row.type,
                                                                                s.value,
                                                                                row.selectAll,
                                                                            )
                                                                        }
                                                                        selected={
                                                                            (
                                                                                selectedTypes?.find(
                                                                                    (s) => s.key === row.type,
                                                                                )?.data || []
                                                                            ).indexOf(s.value) !== -1
                                                                        }
                                                                        style={{ minHeight: '2.5rem' }}
                                                                        className="h-full justify-center items-center px-2 py-1 text-center"
                                                                    >
                                                                        {/* {s.img && (
                                                                <img
                                                                    className="w-5 h-5 rounded-full mr-1 border"
                                                                    src={s.img}
                                                                    alt={s.value}
                                                                />
                                                            )} */}
                                                                        {parser(s.html)}
                                                                    </OnboardingCard>
                                                                </Col>
                                                            ))}
                                                    </Row>
                                                </div>
                                            </div>
                                        ))}

                                    <div className="flex justify-center items-center my-4 w-full">
                                        <HSButton
                                            title={'Next'}
                                            onClick={onClickNext}
                                            className="px-8 "
                                            type="primary"
                                            loading={loader}
                                        />
                                        {isSetting && onClickCancel && (
                                            <HSButton
                                                className="onboarding_btn ml-2"
                                                light
                                                title="Cancel"
                                                onClick={onClickCancel}
                                            />
                                        )}
                                    </div>
                                    {!isSetting && (
                                        <div className="flex justify-center items-center mb-4 w-full">
                                            <HSButton
                                                title={
                                                    investorType !== 'Institutional'
                                                        ? 'Skip to Accreditation'
                                                        : 'Skip to KYC'
                                                }
                                                type="link"
                                                onClick={onClickskipKYC}
                                                style={{ color: '#142C53' }}
                                                className="text-base underline color-1B3E8C"
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
        </OnboardingContainer>
    );
};

export default InvestmentPreferences;

type CompletedProps = {
    setType: (f: string | undefined) => void;
};

export const InvestmentPreferencesCompleted: React.FC<CompletedProps> = ({ setType }) => {
    const navigate = useNavigate();
    const investorType = useSelector((state: RootState) => state.user.investor?.investorType);

    const redirectToPageKYC = () => {
        if (investorType === 'Institutional') {
            setType('Manual');
        } else {
            setType(undefined);
        }
    };

    const redirectToDeal = () => {
        navigate(HEADERMENU_PATH.deals);
    };

    return (
        <div>
            <div className="flex justify-center items-center mb-2">
                <img src={AccountCreated} alt="account_created" />
            </div>
            <p className="text-[#20222B] text-center text-xl WorkSans-Semibold">
                Congratulations! Your account is created
            </p>
            <p className="font-[WorkSans] text-[#20222B] text-base text-center">
                Our team is reviewing your submission and will grant you access to Helicap's live deals upon
                confirmation
            </p>
            <div className="pt-6">
                <OnboardingCard>
                    <div className="flex justify-center flex-col w-full px-2 pt-3 md:px-8">
                        <div className="flex justify-center items-center pb-5">
                            <p className="text-[#26AE60] font-semibold text-sm">Recommended</p>
                            {/* <HSButton
                                light
                                title="Recommended"
                                type="primary"
                                style={{
                                    backgroundColor: '#fff',
                                    color: '#58E866',
                                    borderColor: '#58E866',
                                    textTransform: 'capitalize',
                                }}
                            /> */}
                        </div>
                        <p className="text-center text-base w-full m-auto text-[#20222B]">
                            Completing your KYC is a regulatory requirement that will allow you to invest into our
                            exclusive deal opportunities
                        </p>
                        <div className="flex justify-center items-center pt-8 pb-3">
                            <HSButton title="COMPLETE KYC" type="primary" onClick={redirectToPageKYC} />
                        </div>
                    </div>
                </OnboardingCard>
                <OnboardingCard>
                    <div className="flex justify-center flex-col w-full px-2 pt-3 md:px-8">
                        <p className="text-center text-base w-full m-auto text-[#20222B]">
                            I prefer to come back later to complete my KYC, I want to start my investment journey with
                            Helicap
                        </p>
                        <div className="flex justify-center items-center pt-8 pb-3">
                            <HSButton
                                title="VIEW DEALS"
                                className="onboarding_btn_modal_close"
                                style={{ fontWeight: 600 }}
                                onClick={redirectToDeal}
                            />
                        </div>
                    </div>
                </OnboardingCard>
            </div>
        </div>
    );
};
